import { CORRELATION_ID_HEADER, generateCorrelationId } from "./correlationId";

const TELEMETRY_ENDPOINT_PATH = "/frontend-telemetry/v1/logs";
export const TELEMETRY_SOURCE = "hellocashflow-admin";
const SESSION_LOG_STORAGE_KEY = "HC_SESSION_LOGS";
const SESSION_LOG_FLUSH_DELAY_MS = 1_000;
const SESSION_LOG_MAX_RECORDS = 1_500;
const DIAGNOSTICS_BUFFER_MAX_RECORDS = 400;
const DIAGNOSTICS_SNAPSHOT_MAX_RECORDS = 50;
const DIAGNOSTICS_SNAPSHOT_LOOKBACK_MS = 10 * 60 * 1_000;
const DIAGNOSTICS_MAX_MESSAGE_CHARS = 320;
const DIAGNOSTICS_MAX_ATTRIBUTE_CHARS = 180;
const DIAGNOSTICS_MAX_JSON_CHARS = 35_000;
const EMISSION_MAX_STRING_CHARS = 1_000;
const EMISSION_MAX_ERROR_STACK_CHARS = 4_000;
const EMISSION_MAX_ARRAY_LENGTH = 25;
const EMISSION_MAX_DEPTH = 6;
const REDACTED_VALUE = "[REDACTED]";

type TelemetryMode = "on" | "off";

export interface FrontendTelemetryError {
  name?: string;
  message: string;
  stack?: string;
}

/**
 * Payload sent to `POST /frontend-telemetry/v1/logs`.
 *
 * @remarks
 * The backend accepts either a single record object or an array of records.
 *
 * @see services/api/src/controllers/frontendTelemetryController.ts
 * @see docs/LOGGING.md (Logz.io `type` tagging contract)
 */
export interface FrontendTelemetryLogRecord {
  timestamp: string;
  level: string;
  severityText: string;
  message: string;
  attributes?: Record<string, unknown>;
  error?: FrontendTelemetryError;
  source?: string;
}

type FrontendDiagnosticsAttributeValue = string | number | boolean | null;

export interface FrontendDiagnosticsSnapshotRecord {
  timestamp: string;
  level: "trace" | "debug" | "info" | "warn" | "error" | "fatal";
  message: string;
  route: string | null;
  attributes: Record<string, FrontendDiagnosticsAttributeValue>;
  error: {
    name: string | null;
    message: string | null;
  } | null;
}

export interface FrontendDiagnosticsSnapshot {
  capturedAt: string;
  browserContext: {
    timezone: string;
    language: string;
    online: boolean;
    viewport: {
      width: number;
      height: number;
      devicePixelRatio: number;
    };
    userAgent: string;
  };
  records: FrontendDiagnosticsSnapshotRecord[];
}

const DIAGNOSTICS_LEVEL_ALLOWLIST = new Set([
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "fatal",
]);
const DIAGNOSTICS_ATTRIBUTE_ALLOWLIST = new Set([
  "operation",
  "component",
  "route",
  "correlationId",
  "organizationId",
  "origin",
  "hostname",
  "service",
  "userStatus",
  "sessionId",
  "environment",
  "deployStage",
  "buildVersion",
  "requestId",
]);
const SENSITIVE_ATTRIBUTE_KEYS = new Set([
  "access_token",
  "accessToken",
  "apiKey",
  "api_key",
  "auth",
  "authorization",
  "client_secret",
  "clientSecret",
  "cookie",
  "cookies",
  "email",
  "id_token",
  "idToken",
  "inviteCode",
  "invite_code",
  "password",
  "previewToken",
  "refresh_token",
  "refreshToken",
  "secret",
  "sessionCookie",
  "sessionToken",
  "token",
  "userEmail",
]);

const readEnvValue = (key: string): string | undefined =>
  (import.meta.env as Record<string, string | undefined>)[key];

const truncateTelemetryString = (
  value: string,
  maxChars: number = EMISSION_MAX_STRING_CHARS,
): string => (value.length > maxChars ? `${value.slice(0, maxChars)}…` : value);

const sanitizeUrlLikeString = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  const isAbsoluteUrl = /^https?:\/\//i.test(trimmed);
  const isRelativeUrl = trimmed.startsWith("/") && trimmed.includes("?");

  if (!isAbsoluteUrl && !isRelativeUrl) {
    return trimmed;
  }

  try {
    const parsed = isAbsoluteUrl
      ? new URL(trimmed)
      : new URL(trimmed, "http://redacted.local");
    const keys = Array.from(new Set(parsed.searchParams.keys()));
    const queryKeySuffix =
      keys.length > 0
        ? `?${keys.map((key) => encodeURIComponent(key)).join("&")}`
        : "";
    const base = isAbsoluteUrl ? parsed.origin : "";
    return `${base}${parsed.pathname}${queryKeySuffix}`;
  } catch {
    const [pathOnly] = trimmed.split(/[?#]/, 1);
    return pathOnly;
  }
};

const sanitizeEmbeddedUrlText = (value: string): string => {
  const absoluteUrlPattern = /https?:\/\/[^\s)]+/gi;
  const relativeUrlPattern = /(^|[\s(])(\/(?!\/)[^\s)]*\?[^\s)]+)/g;

  return value
    .replace(absoluteUrlPattern, (match) => sanitizeUrlLikeString(match))
    .replace(
      relativeUrlPattern,
      (_match, prefix: string, pathWithQuery: string) =>
        `${prefix}${sanitizeUrlLikeString(pathWithQuery)}`,
    );
};

const sanitizeEmissionString = (
  key: string,
  value: string,
  maxChars: number = EMISSION_MAX_STRING_CHARS,
): string => {
  if (key === "route" || key === "pathname") {
    return sanitizeRoutePath(value) ?? "";
  }

  if (key === "origin") {
    return truncateTelemetryString(sanitizeUrlLikeString(value), maxChars);
  }

  return truncateTelemetryString(
    sanitizeEmbeddedUrlText(value.trim()),
    maxChars,
  );
};

const normalizeOrganisationAliases = (
  attributes: Record<string, unknown>,
): Record<string, unknown> => {
  const organisationId =
    typeof attributes.organisationId === "string" &&
    attributes.organisationId.trim().length > 0
      ? attributes.organisationId.trim()
      : typeof attributes.organizationId === "string" &&
          attributes.organizationId.trim().length > 0
        ? attributes.organizationId.trim()
        : null;

  if (!organisationId) {
    return attributes;
  }

  return {
    ...attributes,
    organisationId,
    organizationId: organisationId,
  };
};

const sanitizeEmissionValue = (
  key: string,
  value: unknown,
  depth = 0,
): unknown => {
  if (SENSITIVE_ATTRIBUTE_KEYS.has(key)) {
    return REDACTED_VALUE;
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (typeof value === "string") {
    return sanitizeEmissionString(key, value);
  }

  if (depth >= EMISSION_MAX_DEPTH) {
    return "[TRUNCATED]";
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, EMISSION_MAX_ARRAY_LENGTH)
      .map((item) => sanitizeEmissionValue(key, item, depth + 1));
  }

  if (typeof value === "function") {
    return `[function ${value.name || "anonymous"}]`;
  }

  if (typeof value === "symbol") {
    return value.description ? `Symbol(${value.description})` : "Symbol()";
  }

  if (typeof value !== "object") {
    return "[UNSUPPORTED]";
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const sanitizedEntries = Object.entries(value as Record<string, unknown>).map(
    ([nestedKey, nestedValue]) => [
      nestedKey,
      sanitizeEmissionValue(nestedKey, nestedValue, depth + 1),
    ],
  );

  return normalizeOrganisationAliases(Object.fromEntries(sanitizedEntries));
};

const sanitizeTelemetryAttributesForEmission = (
  attributes?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!attributes) {
    return undefined;
  }

  return normalizeOrganisationAliases(
    Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [
        key,
        sanitizeEmissionValue(key, value),
      ]),
    ),
  );
};

const sanitizeTelemetryErrorForEmission = (
  error?: FrontendTelemetryError,
): FrontendTelemetryError | undefined => {
  if (!error) {
    return undefined;
  }

  return {
    ...(error.name
      ? { name: sanitizeEmissionString("errorName", error.name) }
      : {}),
    message: sanitizeEmissionString("errorMessage", error.message),
    ...(error.stack
      ? {
          stack: sanitizeEmissionString(
            "errorStack",
            error.stack,
            EMISSION_MAX_ERROR_STACK_CHARS,
          ),
        }
      : {}),
  };
};

export const sanitizeFrontendTelemetryRecordForEmission = (
  record: FrontendTelemetryLogRecord,
): FrontendTelemetryLogRecord => ({
  ...record,
  message: sanitizeEmissionString("message", record.message),
  ...(record.attributes
    ? {
        attributes: sanitizeTelemetryAttributesForEmission(record.attributes),
      }
    : {}),
  ...(record.error
    ? { error: sanitizeTelemetryErrorForEmission(record.error) }
    : {}),
});

/**
 * Determines whether frontend telemetry should be emitted.
 *
 * @remarks
 * The backend telemetry endpoint is origin-allowlisted. In local/dev environments it is
 * commonly not configured for the admin origin, which can create noisy console 403s.
 *
 * Default behaviour: enabled in all environments (except tests).
 *
 * Override with `VITE_FRONTEND_TELEMETRY_MODE`:
 * - `on`  => emit (default; except in tests)
 * - `off` => never emit (useful when debugging without backend telemetry noise)
 */
const getTelemetryMode = (): TelemetryMode => {
  const configured = readEnvValue("VITE_FRONTEND_TELEMETRY_MODE");
  const normalized = configured?.trim().toLowerCase();

  if (normalized === "off") return "off";
  return "on";
};

const resolveTelemetryEndpoint = (): string | null => {
  const baseUrl = readEnvValue("VITE_API_URL");

  if (!baseUrl?.trim()) {
    return null;
  }

  try {
    return new URL(TELEMETRY_ENDPOINT_PATH, baseUrl).toString();
  } catch {
    return null;
  }
};

const isSessionLogEnabled = (): boolean => {
  if (import.meta.env.MODE === "test") {
    return false;
  }

  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".local")
  );
};

const sessionLogBuffer: FrontendTelemetryLogRecord[] = [];
const diagnosticsBuffer: FrontendTelemetryLogRecord[] = [];
let sessionLogFlushTimeout: ReturnType<typeof setTimeout> | null = null;
let hasSessionClearListener = false;

const pruneSessionLogBuffer = (): void => {
  const targetLength = SESSION_LOG_MAX_RECORDS;
  if (sessionLogBuffer.length <= targetLength) {
    return;
  }

  const excess = sessionLogBuffer.length - targetLength;
  sessionLogBuffer.splice(0, excess);
};

const writeSessionLogsToStorage = (): void => {
  if (!isSessionLogEnabled() || typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      SESSION_LOG_STORAGE_KEY,
      JSON.stringify(sessionLogBuffer),
    );
  } catch {
    // Ignore storage failures; local logs are best-effort and should not block UX.
  }
};

const flushSessionLogs = (): Promise<void> => {
  return Promise.resolve().then(() => {
    if (!isSessionLogEnabled()) {
      return;
    }

    writeSessionLogsToStorage();
  });
};

const scheduleSessionLogWrite = (): void => {
  if (sessionLogFlushTimeout) {
    return;
  }

  sessionLogFlushTimeout = setTimeout(() => {
    sessionLogFlushTimeout = null;
    void flushSessionLogs();
  }, SESSION_LOG_FLUSH_DELAY_MS);
};

const clearSessionLogs = (): void => {
  sessionLogBuffer.length = 0;
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(SESSION_LOG_STORAGE_KEY);
    localStorage.setItem(SESSION_LOG_STORAGE_KEY, "[]");
  } catch {
    // Keep local session logs best-effort.
  }
};

const registerSessionLogClearListener = (): void => {
  if (typeof window === "undefined" || hasSessionClearListener) {
    return;
  }

  window.addEventListener("HC_CLEAR_LOGS", clearSessionLogs);
  hasSessionClearListener = true;
};

const initializeSessionLogging = (): void => {
  if (!isSessionLogEnabled()) {
    return;
  }

  registerSessionLogClearListener();
  clearSessionLogs();
};

const pushSessionLog = (record: FrontendTelemetryLogRecord): void => {
  if (!isSessionLogEnabled()) {
    return;
  }

  sessionLogBuffer.push(record);
  pruneSessionLogBuffer();
  scheduleSessionLogWrite();
};

const pruneDiagnosticsBuffer = (): void => {
  if (diagnosticsBuffer.length <= DIAGNOSTICS_BUFFER_MAX_RECORDS) {
    return;
  }

  diagnosticsBuffer.splice(
    0,
    diagnosticsBuffer.length - DIAGNOSTICS_BUFFER_MAX_RECORDS,
  );
};

const pushDiagnosticsLog = (record: FrontendTelemetryLogRecord): void => {
  diagnosticsBuffer.push(record);
  pruneDiagnosticsBuffer();
};

const sanitizeDiagnosticText = (value: string, maxChars: number): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  const withoutAbsoluteUrls = trimmed.replace(
    /https?:\/\/[^\s)]+/gi,
    "[redacted-url]",
  );
  const withoutQuery = withoutAbsoluteUrls.replace(
    /([/?#][^\s]*)\?([^\s]+)/g,
    (_, basePath: string) => basePath,
  );

  return withoutQuery.length > maxChars
    ? `${withoutQuery.slice(0, maxChars)}…`
    : withoutQuery;
};

const sanitizeRoutePath = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.pathname.slice(0, DIAGNOSTICS_MAX_ATTRIBUTE_CHARS) || "/";
  } catch {
    const withoutHash = trimmed.split("#", 1)[0] ?? trimmed;
    const withoutQuery = withoutHash.split("?", 1)[0] ?? withoutHash;
    if (!withoutQuery) {
      return null;
    }
    return withoutQuery.slice(0, DIAGNOSTICS_MAX_ATTRIBUTE_CHARS);
  }
};

const sanitizeDiagnosticsAttributeValue = (
  key: string,
  value: unknown,
): FrontendDiagnosticsAttributeValue => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value !== "string") {
    return null;
  }

  if (key === "route") {
    return sanitizeRoutePath(value);
  }

  const sanitized = sanitizeDiagnosticText(
    value,
    DIAGNOSTICS_MAX_ATTRIBUTE_CHARS,
  );
  return sanitized || null;
};

const sanitizeDiagnosticsAttributes = (
  attributes: FrontendTelemetryLogRecord["attributes"],
): Record<string, FrontendDiagnosticsAttributeValue> => {
  const output: Record<string, FrontendDiagnosticsAttributeValue> = {};
  if (!attributes) {
    return output;
  }

  for (const [key, value] of Object.entries(attributes)) {
    if (!DIAGNOSTICS_ATTRIBUTE_ALLOWLIST.has(key)) {
      continue;
    }

    output[key] = sanitizeDiagnosticsAttributeValue(key, value);
  }

  return output;
};

const shouldIncludeDiagnosticsRecord = (
  record: FrontendTelemetryLogRecord,
  cutoffMs: number,
): boolean => {
  if (!DIAGNOSTICS_LEVEL_ALLOWLIST.has(record.level.toLowerCase())) {
    return false;
  }

  const recordMs = Date.parse(record.timestamp);
  if (!Number.isFinite(recordMs)) {
    return true;
  }

  return recordMs >= cutoffMs;
};

const toDiagnosticsLevel = (
  level: string,
): FrontendDiagnosticsSnapshotRecord["level"] => {
  const normalized = level.toLowerCase();
  if (normalized === "warning") return "warn";
  if (normalized === "notice") return "info";
  if (
    normalized === "emerg" ||
    normalized === "alert" ||
    normalized === "crit"
  ) {
    return "fatal";
  }
  if (DIAGNOSTICS_LEVEL_ALLOWLIST.has(normalized)) {
    return normalized as FrontendDiagnosticsSnapshotRecord["level"];
  }

  return "info";
};

const buildBrowserContext =
  (): FrontendDiagnosticsSnapshot["browserContext"] => {
    if (typeof window === "undefined") {
      return {
        timezone: "Unknown",
        language: "Unknown",
        online: true,
        viewport: {
          width: 0,
          height: 0,
          devicePixelRatio: 1,
        },
        userAgent: "Unavailable",
      };
    }

    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
      language: navigator.language || "Unknown",
      online: navigator.onLine,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1,
      },
      userAgent: sanitizeDiagnosticText(
        navigator.userAgent || "Unknown",
        DIAGNOSTICS_MAX_ATTRIBUTE_CHARS,
      ),
    };
  };

export const buildFrontendDiagnosticsSnapshot =
  (): FrontendDiagnosticsSnapshot => {
    const cutoffMs = Date.now() - DIAGNOSTICS_SNAPSHOT_LOOKBACK_MS;

    const records = diagnosticsBuffer
      .filter((record) => shouldIncludeDiagnosticsRecord(record, cutoffMs))
      .slice(-DIAGNOSTICS_SNAPSHOT_MAX_RECORDS)
      .map<FrontendDiagnosticsSnapshotRecord>((record) => {
        const attributes = sanitizeDiagnosticsAttributes(record.attributes);
        const route =
          typeof attributes.route === "string" ? attributes.route : null;

        return {
          timestamp: record.timestamp,
          level: toDiagnosticsLevel(record.level),
          message: sanitizeDiagnosticText(
            record.message,
            DIAGNOSTICS_MAX_MESSAGE_CHARS,
          ),
          route,
          attributes,
          error: record.error
            ? {
                name:
                  record.error.name && record.error.name.trim().length > 0
                    ? record.error.name.trim().slice(0, 100)
                    : null,
                message:
                  record.error.message && record.error.message.trim().length > 0
                    ? sanitizeDiagnosticText(
                        record.error.message,
                        DIAGNOSTICS_MAX_MESSAGE_CHARS,
                      )
                    : null,
              }
            : null,
        };
      });

    const snapshot: FrontendDiagnosticsSnapshot = {
      capturedAt: new Date().toISOString(),
      browserContext: buildBrowserContext(),
      records,
    };

    while (
      snapshot.records.length > 0 &&
      JSON.stringify(snapshot).length > DIAGNOSTICS_MAX_JSON_CHARS
    ) {
      snapshot.records.shift();
    }

    return snapshot;
  };

export const resetFrontendDiagnosticsSnapshotBufferForTests = (): void => {
  diagnosticsBuffer.length = 0;
};

initializeSessionLogging();

class FrontendTelemetrySink {
  private readonly endpoint: string | null = resolveTelemetryEndpoint();
  private readonly queue: FrontendTelemetryLogRecord[] = [];
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;

  enqueue(record: FrontendTelemetryLogRecord) {
    if (import.meta.env.MODE === "test" || !this.endpoint) {
      return;
    }

    const mode = getTelemetryMode();
    if (mode === "off") {
      return;
    }

    this.queue.push(record);

    // Defensive bound: prevent unbounded memory growth if the sink is misconfigured.
    const maxQueuedRecords = 50;
    if (this.queue.length > maxQueuedRecords) {
      this.queue.splice(0, this.queue.length - maxQueuedRecords);
    }

    // Flush quickly for error-level events; batch for lower levels.
    const normalizedLevel = record.level.toLowerCase();
    const flushSoon =
      normalizedLevel === "error" || normalizedLevel === "fatal";

    this.scheduleFlush(flushSoon ? 0 : 1_000);
  }

  async flush(): Promise<void> {
    await this.flushQueue();
  }

  private scheduleFlush(delayMs: number) {
    if (this.flushTimeout) {
      return;
    }

    this.flushTimeout = setTimeout(() => {
      this.flushTimeout = null;
      void this.flush().catch(() => {
        // Best-effort: avoid surfacing telemetry failures to the UI.
      });
    }, delayMs);
  }

  private async flushQueue(): Promise<void> {
    if (this.isFlushing || !this.endpoint || this.queue.length === 0) {
      return;
    }

    this.isFlushing = true;

    try {
      // Keep request payloads small to avoid hitting backend size limits and keepalive constraints.
      const batch = this.queue.splice(0, 25);
      const correlationId = this.resolveBatchCorrelationId(batch);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3_000);

      try {
        await fetch(this.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            [CORRELATION_ID_HEADER]: correlationId,
          },
          body: JSON.stringify(batch.length === 1 ? batch[0] : batch),
          keepalive: true,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    } finally {
      this.isFlushing = false;

      if (this.queue.length > 0) {
        this.scheduleFlush(1_000);
      }
    }
  }

  private resolveBatchCorrelationId(
    batch: FrontendTelemetryLogRecord[],
  ): string {
    for (const record of batch) {
      const correlationId = record.attributes?.correlationId;
      if (typeof correlationId === "string" && correlationId.trim()) {
        return correlationId.trim();
      }
    }

    return generateCorrelationId();
  }
}

const telemetrySink = new FrontendTelemetrySink();

export const enqueueFrontendTelemetryRecord = (
  record: FrontendTelemetryLogRecord,
): void => {
  const sanitizedRecord = sanitizeFrontendTelemetryRecordForEmission(record);
  pushDiagnosticsLog(sanitizedRecord);
  telemetrySink.enqueue(sanitizedRecord);
  pushSessionLog(sanitizedRecord);
};

export const flushFrontendTelemetry = async (): Promise<void> => {
  await Promise.all([telemetrySink.flush(), flushSessionLogs()]);
};

export const getFrontendSessionLogStorageKey = (): string =>
  SESSION_LOG_STORAGE_KEY;
